import type { ReactNode } from "react";
import { px } from "@/components/tool-kit/charts/marks";
import {
  CHART_FONT_SIZE,
  type Frame,
  type TitleBlock,
} from "@/lib/tool-kit/charts/layout";
import { CHART_FONT } from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/**
 * The root of every chart and diagram: an `<svg>` that carries everything it needs to
 * look the same outside the page.
 *
 * The exported SVG and PNG see none of the site's CSS, so this sets the font family,
 * paints its own white background, and leaves every fill and size to attributes on the
 * marks. It scales with its container through `viewBox`; the `width`/`height`
 * attributes are the size a downloaded file opens at.
 *
 * `label` and `description` become the `<title>` and `<desc>` a screen reader announces
 * (`role="img"`). The label is required even for an APA figure, whose visible title
 * lives outside the image — the image still needs a name.
 */
export function ChartSvg({
  id,
  width,
  height,
  label,
  description,
  fontFamily = CHART_FONT,
  className = "block h-auto w-full",
  children,
}: {
  /** Unique on the page; prefixes the title and desc ids. */
  id: string;
  width: number;
  height: number;
  label: string;
  description: string;
  fontFamily?: string;
  /**
   * How the preview is sized. The default scales the drawing to its container; a wide
   * diagram (a Gantt chart over many weeks) can keep its natural width inside a
   * scrolling container instead, so its labels stay readable.
   */
  className?: string;
  children: ReactNode;
}) {
  return (
    <svg
      aria-labelledby={`${id}-title ${id}-desc`}
      className={className}
      fontFamily={fontFamily}
      height={Math.round(height)}
      id={id}
      role="img"
      viewBox={`0 0 ${Math.round(width)} ${Math.round(height)}`}
      width={Math.round(width)}
    >
      <title id={`${id}-title`}>{label}</title>
      <desc id={`${id}-desc`}>{description}</desc>
      <rect
        fill={CHART_INK.background}
        height={Math.round(height)}
        width={Math.round(width)}
        x={0}
        y={0}
      />
      {children}
    </svg>
  );
}

/** A title block from `layoutTitle`: centred bold lines. Nothing for a null block. */
export function ChartTitle({ title }: { title: TitleBlock | null }) {
  if (!title) {
    return null;
  }
  return (
    <g>
      {title.lines.map((line, index) => (
        <text
          fill={CHART_INK.text}
          fontSize={title.fontSize}
          fontWeight="bold"
          // Lines of one title: position is their identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional lines
          key={index}
          textAnchor="middle"
          x={px(title.x)}
          y={px(title.y + index * title.lineHeight)}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

/**
 * The words around the plot: the title (absent on an APA figure) and the axis titles,
 * at the positions `cartesianFrame` gave them.
 */
export function ChartTitles({ frame }: { frame: Frame }) {
  const { title, xTitle, yTitle } = frame;
  return (
    <g>
      <ChartTitle title={title} />
      {xTitle ? (
        <text
          fill={CHART_INK.text}
          fontSize={CHART_FONT_SIZE.axisTitle}
          textAnchor="middle"
          x={px(xTitle.x)}
          y={px(xTitle.y)}
        >
          {xTitle.text}
        </text>
      ) : null}
      {yTitle ? (
        <text
          fill={CHART_INK.text}
          fontSize={CHART_FONT_SIZE.axisTitle}
          textAnchor="middle"
          transform={`rotate(-90 ${px(yTitle.x)} ${px(yTitle.y)})`}
          x={px(yTitle.x)}
          y={px(yTitle.y)}
        >
          {yTitle.text}
        </text>
      ) : null}
    </g>
  );
}
